/**
 * Cheatsheet — keyboard-shortcut reference modal (M3 Phase 3.4).
 *
 * Open with `?`, close with `Esc`, click on the backdrop, or click
 * the explicit `关闭` button. Keys are listed in the order that
 * matches the §4.4 navigation flow (movement → decision → mode →
 * help) so the eye scans top-down naturally.
 *
 * Style notes:
 *   - Backdrop uses `bg-ink-900/40` + `backdrop-blur-sm`. Tailwind's
 *     v3 backdrop-blur-sm utility exists in core; we don't need to
 *     extend the theme.
 *   - The modal panel is centred via flex; max width is 480 px so
 *     the table doesn't drift past the comfortable read width on
 *     wide monitors.
 *   - We deliberately do NOT trap focus — the only interactive
 *     control inside is the close button, and `Esc` already exits.
 *     Adding a full focus trap would pull in another dependency.
 */
import { useEffect } from 'react';

interface CheatsheetProps {
  open: boolean;
  onClose: () => void;
}

interface ShortcutRow {
  key: string;
  desc: string;
}

const ROWS: ShortcutRow[] = [
  // Each navigation key lists both spellings: PRD FR-4 declares N / P, the
  // earlier build shipped with j / k. Both work — the cheatsheet makes the
  // equivalence visible so reviewers aren't surprised either way.
  { key: 'j / n', desc: '下一处变更' },
  { key: 'k / p', desc: '上一处变更' },
  { key: 'a', desc: '接受当前变更' },
  { key: 'r', desc: '拒绝当前变更' },
  { key: 'z', desc: '重置为待定（撤销决策）' },
  { key: 'c', desc: '为当前变更添加标注' },
  { key: 'f', desc: '切换聚焦阅读模式' },
  { key: 'v', desc: '切换双栏 / 统一视图' },
  { key: '?', desc: '显示快捷键' },
  { key: 'Esc', desc: '关闭浮层 / 取消焦点' },
];

export function Cheatsheet({ open, onClose }: CheatsheetProps): React.JSX.Element | null {
  // Lock background scroll while open so the user can't accidentally
  // drift the comparison view behind the modal.
  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="cheatsheet-title"
      className="fixed inset-0 z-modal-overlay flex items-center justify-center bg-ink-900/40 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="z-modal-content w-full max-w-[480px] rounded-md border border-ink-200 bg-ink-0 p-6 shadow-lg"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2
            id="cheatsheet-title"
            className="text-lg font-semibold text-ink-900"
          >
            键盘快捷键
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-sm px-2 py-1 text-xs text-ink-500 hover:bg-ink-100 hover:text-ink-900"
            aria-label="关闭快捷键面板"
          >
            关闭 (Esc)
          </button>
        </div>

        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-ink-200 text-left text-xs uppercase tracking-wide text-ink-500">
              <th scope="col" className="w-24 py-2 pr-4 font-medium">
                按键
              </th>
              <th scope="col" className="py-2 font-medium">
                操作
              </th>
            </tr>
          </thead>
          <tbody>
            {ROWS.map((row) => (
              <tr key={row.key} className="border-b border-ink-100 last:border-b-0">
                <td className="py-2 pr-4">
                  <kbd className="inline-flex h-6 min-w-6 items-center justify-center rounded-sm border border-ink-200 bg-ink-50 px-2 font-mono text-xs text-ink-900">
                    {row.key}
                  </kbd>
                </td>
                <td className="py-2 text-ink-700">{row.desc}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <p className="mt-4 text-xs text-ink-500">
          提示：当焦点位于输入框或与浏览器原生快捷键冲突时，本页快捷键不会触发。
        </p>
      </div>
    </div>
  );
}

export default Cheatsheet;
